import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Label } from '../../../components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import { Badge } from '../../../components/ui/Badge';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/Alert';
import toast from 'react-hot-toast';
import {
  CreditCard, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  CheckCircle, 
  X, 
  Star, 
  Users, 
  Zap, 
  Shield, 
  Clock, 
  DollarSign, 
  Calendar, 
  Settings,
  AlertCircle,
  Save,
  RefreshCw
} from 'lucide-react';

const SubscriptionPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [saving, setSaving] = useState(false);
  const [newPlan, setNewPlan] = useState({
    name: '',
    description: '',
    price: 0,
    billingCycle: 'monthly',
    status: 'draft',
    features: [],
    limitations: [],
    subscribers: 0,
    revenue: 0,
    isPopular: false,
    trialDays: 0,
    setupFee: 0
  });
  const [featureInput, setFeatureInput] = useState('');
  const [limitationInput, setLimitationInput] = useState('');

  const mockPlans = [
    {
      id: 'PLAN-001',
      name: 'Starter',
      description: 'Perfect for small teams getting started',
      price: 29,
      billingCycle: 'monthly',
      status: 'active',
      features: [
        '100 tickets per month',
        'Basic email support',
        'Standard SLA (24 hours)',
        'Basic reporting',
        'Up to 5 users'
      ],
      limitations: [
        'No API access',
        'No custom fields'
      ],
      subscribers: 45,
      revenue: 1305,
      createdAt: '2023-01-15',
      lastModified: '2023-10-20',
      isPopular: false,
      trialDays: 14,
      setupFee: 0
    },
    {
      id: 'PLAN-002',
      name: 'Professional',
      description: 'Ideal for growing businesses',
      price: 99,
      billingCycle: 'monthly',
      status: 'active',
      features: [
        '500 tickets per month',
        'Priority email support',
        'Fast SLA (8 hours)',
        'Advanced reporting',
        'Up to 15 users',
        'API access',
        'Custom fields'
      ],
      limitations: [
        'No phone support',
        'No custom integrations'
      ],
      subscribers: 120,
      revenue: 11880,
      createdAt: '2023-01-15',
      lastModified: '2023-10-18',
      isPopular: true,
      trialDays: 30,
      setupFee: 0
    },
    {
      id: 'PLAN-003',
      name: 'Enterprise',
      description: 'For large organizations with complex needs',
      price: 199,
      billingCycle: 'monthly',
      status: 'active',
      features: [
        '1000 tickets per month',
        'Phone & email support',
        'Premium SLA (4 hours)',
        'Unlimited users',
        'Full API access',
        'Custom integrations',
        'White-label options',
        'Dedicated account manager'
      ],
      limitations: [],
      subscribers: 35,
      revenue: 6965,
      createdAt: '2023-01-15',
      lastModified: '2023-10-15',
      isPopular: false,
      trialDays: 30,
      setupFee: 500
    },
    {
      id: 'PLAN-004',
      name: 'Enterprise Pro',
      description: 'Ultimate solution for enterprise customers',
      price: 299,
      billingCycle: 'monthly',
      status: 'active',
      features: [
        'Unlimited tickets',
        '24/7 phone support',
        'Critical SLA (2 hours)',
        'Unlimited users',
        'Full API access',
        'Custom integrations',
        'White-label options',
        'Dedicated account manager',
        'Custom training',
        'SLA guarantees'
      ],
      limitations: [],
      subscribers: 12,
      revenue: 3588,
      createdAt: '2023-01-15',
      lastModified: '2023-10-10',
      isPopular: false,
      trialDays: 30,
      setupFee: 1000
    },
    {
      id: 'PLAN-005',
      name: 'Legacy Basic',
      description: 'Legacy plan for existing customers',
      price: 19,
      billingCycle: 'monthly',
      status: 'deprecated',
      features: [
        '50 tickets per month',
        'Email support only',
        'Standard SLA (48 hours)',
        'Basic reporting',
        'Up to 3 users'
      ],
      limitations: [
        'No API access',
        'No custom fields',
        'Limited support'
      ],
      subscribers: 8,
      revenue: 152,
      createdAt: '2022-06-01',
      lastModified: '2023-09-30',
      isPopular: false,
      trialDays: 7,
      setupFee: 0
    }
  ];

  const filteredPlans = plans
    .filter(plan =>
      plan.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plan.description.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter(plan => filterStatus === 'all' || plan.status === filterStatus)
    .sort((a, b) => {
      if (a.isPopular && !b.isPopular) return -1;
      if (!a.isPopular && b.isPopular) return 1;
      return b.price - a.price;
    });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active': return <Badge variant="success">Active</Badge>;
      case 'deprecated': return <Badge variant="warning">Deprecated</Badge>;
      case 'draft': return <Badge variant="secondary">Draft</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  useEffect(() => {
    // Simulate loading plans
    const loadPlans = () => {
      setLoading(true);
      setTimeout(() => {
        setPlans(mockPlans);
        setLoading(false);
      }, 1000);
    };

    loadPlans();
  }, []);

  const handleDeletePlan = (planId) => {
    setPlans(plans.filter(plan => plan.id !== planId));
  };

  const handleToggleStatus = (planId) => {
    setPlans(plans.map(plan => 
      plan.id === planId 
        ? { ...plan, status: plan.status === 'active' ? 'deprecated' : 'active' }
        : plan
    ));
  };

  const handleCreatePlan = () => {
    setNewPlan({
      name: '',
      description: '',
      price: 0,
      billingCycle: 'monthly',
      status: 'draft',
      features: [],
      limitations: [],
      subscribers: 0,
      revenue: 0,
      isPopular: false,
      trialDays: 0,
      setupFee: 0
    });
    setFeatureInput('');
    setLimitationInput('');
    setShowAddModal(true);
  };

  const handleAddFeature = () => {
    if (featureInput.trim()) {
      setNewPlan({
        ...newPlan,
        features: [...newPlan.features, featureInput.trim()]
      });
      setFeatureInput('');
    }
  };

  const handleRemoveFeature = (index) => {
    setNewPlan({
      ...newPlan,
      features: newPlan.features.filter((_, i) => i !== index)
    });
  };

  const handleAddLimitation = () => {
    if (limitationInput.trim()) {
      setNewPlan({
        ...newPlan,
        limitations: [...newPlan.limitations, limitationInput.trim()]
      });
      setLimitationInput('');
    }
  };

  const handleRemoveLimitation = (index) => {
    setNewPlan({
      ...newPlan,
      limitations: newPlan.limitations.filter((_, i) => i !== index)
    });
  };

  const handleSavePlan = async () => {
    if (!newPlan.name.trim()) {
      toast.error('Plan name is required');
      return;
    }
    if (newPlan.price <= 0) {
      toast.error('Plan price must be greater than 0');
      return;
    }

    try {
      setSaving(true);
      const planData = {
        id: editingPlan ? editingPlan.id : `PLAN-${Date.now()}`,
        ...newPlan,
        createdAt: editingPlan ? editingPlan.createdAt : new Date().toISOString().split('T')[0],
        lastModified: new Date().toISOString().split('T')[0]
      };

      if (editingPlan) {
        setPlans(plans.map(plan => plan.id === editingPlan.id ? planData : plan));
        toast.success('Plan updated successfully');
      } else {
        setPlans([...plans, planData]);
        toast.success('Plan created successfully');
      }

      setShowAddModal(false);
      setEditingPlan(null);
      setNewPlan({
        name: '',
        description: '',
        price: 0,
        billingCycle: 'monthly',
        status: 'draft',
        features: [],
        limitations: [],
        subscribers: 0,
        revenue: 0,
        isPopular: false,
        trialDays: 0,
        setupFee: 0
      });
      setFeatureInput('');
      setLimitationInput('');
    } catch (err) {
      toast.error('Failed to save plan');
      console.error('Error saving plan:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setNewPlan({
      name: plan.name,
      description: plan.description,
      price: plan.price,
      billingCycle: plan.billingCycle,
      status: plan.status,
      features: [...plan.features],
      limitations: [...plan.limitations],
      subscribers: plan.subscribers,
      revenue: plan.revenue,
      isPopular: plan.isPopular,
      trialDays: plan.trialDays,
      setupFee: plan.setupFee
    });
    setFeatureInput('');
    setLimitationInput('');
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingPlan(null);
    setNewPlan({
      name: '',
      description: '',
      price: 0,
      billingCycle: 'monthly',
      status: 'draft',
      features: [],
      limitations: [],
      subscribers: 0,
      revenue: 0,
      isPopular: false,
      trialDays: 0,
      setupFee: 0
    });
    setFeatureInput('');
    setLimitationInput('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading subscription plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Subscription Plans</h1>
        <Button variant="primary" onClick={handleCreatePlan} className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-md">
          <Plus className="h-4 w-4" /> New Plan
        </Button>
      </div>

      {/* Statistics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-blue-700 dark:text-blue-300">Total Plans</CardTitle>
            <CreditCard className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-800 dark:text-blue-200">{plans.length}</div>
            <p className="text-xs text-blue-600 dark:text-blue-400">Active plans</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-green-700 dark:text-green-300">Total Subscribers</CardTitle>
            <Users className="h-4 w-4 text-green-600 dark:text-green-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-800 dark:text-green-200">
              {plans.reduce((sum, plan) => sum + plan.subscribers, 0)}
            </div>
            <p className="text-xs text-green-600 dark:text-green-400">Across all plans</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-purple-700 dark:text-purple-300">Monthly Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-purple-600 dark:text-purple-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-800 dark:text-purple-200">
              {formatCurrency(plans.reduce((sum, plan) => sum + plan.revenue, 0))}
            </div>
            <p className="text-xs text-purple-600 dark:text-purple-400">From subscriptions</p>
          </CardContent>
        </Card>
        <Card className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-yellow-700 dark:text-yellow-300">Popular Plan</CardTitle>
            <Star className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-800 dark:text-yellow-200">
              {plans.find(plan => plan.isPopular)?.name || 'None'}
            </div>
            <p className="text-xs text-yellow-600 dark:text-yellow-400">Most subscribed</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col md:flex-row gap-4 items-center">
        <div className="relative w-full md:w-1/3">
          <Input
            type="text"
            placeholder="Search plans..."
            value={searchTerm}
            onChange={(value) => setSearchTerm(value)}
            className="pr-3 py-2 border rounded-md w-full"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="p-2 border rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="deprecated">Deprecated</option>
            <option value="draft">Draft</option>
          </select>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPlans.map(plan => (
          <Card key={plan.id} className={`relative hover:shadow-lg transition-shadow ${plan.isPopular ? 'ring-2 ring-blue-500' : ''}`}>
            {plan.isPopular && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <Badge variant="primary" className="flex items-center gap-1">
                  <Star className="h-3 w-3" />
                  Most Popular
                </Badge>
              </div>
            )}
            <CardHeader className="text-center">
              <div className="flex justify-between items-start mb-2">
                {getStatusBadge(plan.status)}
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => handleEditPlan(plan)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDeletePlan(plan.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <CardTitle className="text-xl">{plan.name}</CardTitle>
              <p className="text-gray-600 dark:text-gray-400 text-sm">{plan.description}</p>
              <div className="mt-4">
                <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                  {formatCurrency(plan.price)}
                  <span className="text-lg font-normal text-gray-500">/{plan.billingCycle}</span>
                </div>
                {plan.setupFee > 0 && (
                  <div className="text-sm text-gray-500 mt-1">
                    + {formatCurrency(plan.setupFee)} setup fee
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Features */}
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Features</h4>
                  <ul className="space-y-1">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Limitations */}
                {plan.limitations.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Limitations</h4>
                    <ul className="space-y-1">
                      {plan.limitations.map((limitation, index) => (
                        <li key={index} className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                          <X className="h-4 w-4 text-red-500 flex-shrink-0" />
                          {limitation}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Plan Stats */}
                <div className="border-t pt-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Subscribers:</span>
                      <div className="font-medium">{plan.subscribers}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Revenue:</span>
                      <div className="font-medium">{formatCurrency(plan.revenue)}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Trial:</span>
                      <div className="font-medium">{plan.trialDays} days</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Modified:</span>
                      <div className="font-medium">{formatDate(plan.lastModified)}</div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-4">
                  <Button variant="outline" size="sm" className="flex-1">
                    <Eye className="h-4 w-4 mr-1" /> View
                  </Button>
                  <Button 
                    variant={plan.status === 'active' ? 'secondary' : 'primary'} 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleToggleStatus(plan.id)}
                  >
                    {plan.status === 'active' ? 'Deprecate' : 'Activate'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredPlans.length === 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>No Plans Found</AlertTitle>
          <AlertDescription>
            No subscription plans match your current search and filter criteria. Try adjusting your filters or create a new plan.
          </AlertDescription>
        </Alert>
      )}

      {/* Create/Edit Plan Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white shadow-xl">
            <CardHeader className="border-b border-gray-200">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-xl font-semibold text-gray-900">
                  <CreditCard className="h-5 w-5" />
                  {editingPlan ? 'Edit Plan' : 'Create New Plan'}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCloseModal}
                  className="h-8 w-8 p-0 hover:bg-gray-100"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="plan-name" className="text-sm font-medium text-gray-700">
                      Plan Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="plan-name"
                      placeholder="e.g., Starter, Professional"
                      value={newPlan.name}
                      onChange={(value) => setNewPlan({ ...newPlan, name: value })}
                      className="w-full"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="plan-price" className="text-sm font-medium text-gray-700">
                      Price <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="plan-price"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="29.00"
                      value={newPlan.price}
                      onChange={(value) => setNewPlan({ ...newPlan, price: parseFloat(value) || 0 })}
                      className="w-full"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="plan-description" className="text-sm font-medium text-gray-700">
                    Description
                  </Label>
                  <textarea
                    id="plan-description"
                    placeholder="Describe the plan..."
                    value={newPlan.description}
                    onChange={(e) => setNewPlan({ ...newPlan, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
                    rows="3"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="billing-cycle" className="text-sm font-medium text-gray-700">
                      Billing Cycle
                    </Label>
                    <Select
                      value={newPlan.billingCycle}
                      onValueChange={(value) => setNewPlan({ ...newPlan, billingCycle: value })}
                    >
                      <SelectTrigger id="billing-cycle" className="w-full">
                        <SelectValue placeholder="Select cycle" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="plan-status" className="text-sm font-medium text-gray-700">
                      Status
                    </Label>
                    <Select
                      value={newPlan.status}
                      onValueChange={(value) => setNewPlan({ ...newPlan, status: value })}
                    >
                      <SelectTrigger id="plan-status" className="w-full">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="deprecated">Deprecated</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="trial-days" className="text-sm font-medium text-gray-700">
                      Trial Days
                    </Label>
                    <Input
                      id="trial-days"
                      type="number"
                      min="0"
                      placeholder="14"
                      value={newPlan.trialDays}
                      onChange={(value) => setNewPlan({ ...newPlan, trialDays: parseInt(value) || 0 })}
                      className="w-full"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="setup-fee" className="text-sm font-medium text-gray-700">
                      Setup Fee
                    </Label>
                    <Input
                      id="setup-fee"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={newPlan.setupFee}
                      onChange={(value) => setNewPlan({ ...newPlan, setupFee: parseFloat(value) || 0 })}
                      className="w-full"
                    />
                  </div>

                  <div className="space-y-2 flex items-end">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="is-popular"
                        checked={newPlan.isPopular}
                        onChange={(e) => setNewPlan({ ...newPlan, isPopular: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <Label htmlFor="is-popular" className="text-sm font-medium text-gray-700 cursor-pointer">
                        Mark as Popular
                      </Label>
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Features</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add a feature..."
                      value={featureInput}
                      onChange={(value) => setFeatureInput(value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddFeature();
                        }
                      }}
                      className="flex-1"
                    />
                    <Button type="button" onClick={handleAddFeature} variant="outline">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {newPlan.features.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {newPlan.features.map((feature, index) => (
                        <Badge key={index} variant="secondary" className="flex items-center gap-1">
                          {feature}
                          <button
                            type="button"
                            onClick={() => handleRemoveFeature(index)}
                            className="ml-1 hover:text-red-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Limitations */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Limitations</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add a limitation..."
                      value={limitationInput}
                      onChange={(value) => setLimitationInput(value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddLimitation();
                        }
                      }}
                      className="flex-1"
                    />
                    <Button type="button" onClick={handleAddLimitation} variant="outline">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {newPlan.limitations.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {newPlan.limitations.map((limitation, index) => (
                        <Badge key={index} variant="secondary" className="flex items-center gap-1">
                          {limitation}
                          <button
                            type="button"
                            onClick={() => handleRemoveLimitation(index)}
                            className="ml-1 hover:text-red-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                  <Button
                    variant="outline"
                    onClick={handleCloseModal}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSavePlan}
                    disabled={saving || !newPlan.name.trim() || newPlan.price <= 0}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        {editingPlan ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        {editingPlan ? 'Update Plan' : 'Create Plan'}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPlans;
